import DateFnsUtils from '@date-io/date-fns'
import {
  DatePicker as Mui4DatePicker,
  DatePickerProps as Mui4DatePickerPropsType,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import {
  DatePicker as MuiX7DatePicker,
  DatePickerProps as MuiX7DatePickerPropsType,
  PickersActionBarProps,
} from '@mui/x-date-pickers'
import { forwardRef, useState } from 'react'
import { useComponentVersion } from '../hooks'
import { CustomDatePickerHeader } from '../other/datePicker/DatePickerHeader'
import { DATE_PICKER_PROPS, withPickerDayjs } from '../utils/pickerDateValues'
import { Icon } from './Icon'
import { IconButton } from './IconButton'

export enum OsDatePickerVersion {
  V1 = 1, // MUI4 + OS Theme
  V2 = 2, // MUI5 + Stratis
}

export type DatePickerProps = Omit<Mui4DatePickerPropsType, 'views'> & {
  ActionBar?: React.ElementType<PickersActionBarProps>
  views?: MuiX7DatePickerPropsType<any, boolean>['views'] | Mui4DatePickerPropsType['views']
  showDaysOutsideCurrentMonth?: MuiX7DatePickerPropsType<any, boolean>['showDaysOutsideCurrentMonth']
  slotProps?: MuiX7DatePickerPropsType<any, boolean>['slotProps']
  helperText?: string
}

// * MUI DatePicker API Spec  https://mui.com/x/react-date-pickers/date-picker/
export const DatePicker = forwardRef<HTMLDivElement, DatePickerProps>((props, ref) => {
  const version = useComponentVersion('date_picker')
  const [open, setOpen] = useState(false)

  const { slotProps, ...rest } = props

  switch (version) {
    case OsDatePickerVersion.V2: {
      return (
        <MuiX7DatePicker
          ref={ref}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          slots={{
            calendarHeader: CustomDatePickerHeader,
            actionBar: props.ActionBar, // Please refer to the TimePickerActionBar.tsx for an example
          }}
          slotProps={{
            popper: {
              sx: (theme) => ({
                zIndex: 2700,
                '& .MuiDateCalendar-root': {
                  borderBottom: props.ActionBar ? `1px solid ${theme.palette.neutralGrey[700]}` : 'none',
                },
              }),
            },
            textField: (ownerState) => ({
              ...ownerState,
              ...slotProps?.textField,
              InputProps: {
                ...ownerState.InputProps,
                // @ts-ignore
                ...slotProps?.textField?.InputProps,
                endAdornment: (
                  <IconButton
                    sx={{ width: 20, height: 20, '&:hover': { backgroundColor: 'transparent' } }}
                    onClick={() => setOpen(true)}
                  >
                    <Icon icon="calendar_today" size={'small'} />
                  </IconButton>
                ),
              },
              helperText: props.helperText,
              error: props.error,
              onBlur: props.onBlur,
            }),
          }}
          {...withPickerDayjs(rest as MuiX7DatePickerPropsType<any, boolean>, ...DATE_PICKER_PROPS)}
        />
      )
    }

    default:
      return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <Mui4DatePicker ref={ref} {...(props as Mui4DatePickerPropsType)} />
        </MuiPickersUtilsProvider>
      )
  }
})
